import type {
  EmissionsSankeyItem,
  EmissionsByScopeAndMonthResponse,
  EmissionsByScopeAndCategoryResponse,
  SankeyCategory,
  YearEmission,
} from "../types/dashboard";
import type {
  SupplierAllocationPeriod,
  SupplierAllocatedEmissions,
} from "@/features/suppliers/api/supplierService";
import type { EmissionProtocol } from "@/lib/protocol";

/**
 * Supplier allocations are reported by SRM in tCO2e; the dashboard emission
 * endpoints report kgCO2e. Normalise to kg when merging.
 */
export const ALLOCATION_TCO2E_TO_KG = 1000;

export const INVESTMENT_CATEGORY_MAP: Record<string, string> = {
  "Listed equity and corporate bonds": "Category 5.4A",
  "Business loans and unlisted equity": "Category 5.4B",
  "Project finance": "Category 5.4C",
  "Commercial real estate": "Category 5.4D",
  Mortgages: "Category 5.4E",
  "Motor vehicle loans": "Category 5.4F",
  "Sovereign debt": "Category 5.4G",
};

// GHG investment leaves are siblings beneath Scope 3 in the active catalogue.
const GHG_INVESTMENT_CATEGORY_MAP: Record<string, string> = {
  "Category 5.4A": "Scope 3.15.1",
  "Category 5.4B": "Scope 3.15.2",
  "Category 5.4C": "Scope 3.15.3",
  "Category 5.4D": "Scope 3.15.4",
  "Category 5.4E": "Scope 3.15.5",
  "Category 5.4F": "Scope 3.15.6",
  "Category 5.4G": "Scope 3.15.7",
};

function investmentCode(
  entry: InvestmentBreakdown,
  protocol: EmissionProtocol,
) {
  return protocol === "ghg"
    ? (GHG_INVESTMENT_CATEGORY_MAP[entry.code] ?? "Scope 3.15")
    : entry.code;
}

import { monthKey, monthAxis, alignMonths } from "./monthlyData";
export { monthKey } from "./monthlyData";

export interface InvestmentBreakdown {
  name: string;
  code: string;
  totalKg: number;
  monthlyKg: Record<string, number>;
}

export interface SupplierAllocationBreakdown {
  /** Investment and supplier allocations are independent streams and may coexist. */
  totalKg: number;
  supplierTotalKg: number;
  /** Supplier-only monthly kg keyed by "YYYY-MM". */
  monthlyKg: Record<string, number>;
  investorByCode: Record<string, InvestmentBreakdown>;
}

function sumPeriods(periods: SupplierAllocationPeriod[]): number {
  return periods.reduce(
    (sum, period) => sum + (Number(period.allocated_total_emissions) || 0),
    0,
  );
}

export function buildAllocationBreakdown(
  defaultAlloc?: SupplierAllocatedEmissions | null,
  investorAlloc?: SupplierAllocatedEmissions | null,
): SupplierAllocationBreakdown | null {
  const investorPeriods = investorAlloc?.periodic_breakup ?? [];
  const hasInvestments = investorPeriods.length > 0;
  const periods = defaultAlloc?.periodic_breakup ?? [];
  if (!hasInvestments && periods.length === 0) return null;
  const supplierTotalKg = sumPeriods(periods) * ALLOCATION_TCO2E_TO_KG;
  const monthlyKg: Record<string, number> = {};
  for (const period of periods) {
    const key = monthKey(period.month);
    if (key)
      monthlyKg[key] =
        (monthlyKg[key] ?? 0) +
        (Number(period.allocated_total_emissions) || 0) *
          ALLOCATION_TCO2E_TO_KG;
  }

  if (hasInvestments) {
    const byName = new Map<string, InvestmentBreakdown>();
    for (const period of investorPeriods) {
      const name = period.name || "Investments";
      const normalizedName = name.trim().toLowerCase();
      const entry = byName.get(normalizedName);
      const kg =
        (Number(period.allocated_total_emissions) || 0) *
        ALLOCATION_TCO2E_TO_KG;
      if (entry) {
        entry.totalKg += kg;
        const key = monthKey(period.month);
        if (key) entry.monthlyKg[key] = (entry.monthlyKg[key] ?? 0) + kg;
      } else {
        byName.set(normalizedName, {
          name,
          code:
            Object.entries(INVESTMENT_CATEGORY_MAP).find(
              ([label]) => label.toLowerCase() === normalizedName,
            )?.[1] ?? `Category 5.4 (${name})`,
          totalKg: kg,
          monthlyKg: monthKey(period.month)
            ? { [monthKey(period.month)]: kg }
            : {},
        });
      }
    }
    const investorByCode: Record<string, InvestmentBreakdown> = {};
    let totalKg = 0;
    for (const entry of byName.values()) {
      investorByCode[entry.code] = entry;
      totalKg += entry.totalKg;
    }
    return {
      totalKg: totalKg + supplierTotalKg,
      supplierTotalKg,
      monthlyKg,
      investorByCode,
    };
  }

  return {
    totalKg: supplierTotalKg,
    supplierTotalKg,
    monthlyKg,
    investorByCode: {},
  };
}

export function mergeAllocationsIntoSankey(
  items: EmissionsSankeyItem[],
  breakdown: SupplierAllocationBreakdown | null,
  protocol: EmissionProtocol = "iso",
): EmissionsSankeyItem[] {
  if (!breakdown) return items;

  const clones: EmissionsSankeyItem[] = items.map((scope) => ({
    ...scope,
    categories: scope.categories.map((cat) => ({
      ...cat,
      subcategories: cat.subcategories.map((sub) => ({ ...sub })),
    })),
  }));

  if (protocol === "ghg") {
    let total = clones.find((item) => item.code === "Total");
    if (!total) {
      total = { code: "Total", value: 0, categories: [] };
      clones.push(total);
    }
    let scope3 = total.categories.find((cat) => cat.code === "Scope 3");
    if (!scope3) {
      scope3 = {
        code: "Scope 3",
        name: "Scope 3",
        value: 0,
        subcategories: [],
      };
      total.categories.push(scope3);
    }
    const entries = [
      ...Object.values(breakdown.investorByCode).map((entry) => ({
        code: investmentCode(entry, protocol),
        name: entry.name,
        value: entry.totalKg,
      })),
      {
        code: "Scope 3.1",
        name: "Purchased goods & services",
        value: breakdown.supplierTotalKg,
      },
    ];
    for (const entry of entries) {
      if (entry.value <= 0) continue;
      const existing = scope3.subcategories.find(
        (sub) => sub.code === entry.code,
      );
      if (existing) existing.value += entry.value;
      else scope3.subcategories.push({ ...entry });
      scope3.value += entry.value;
      total.value += entry.value;
    }
    return clones;
  }

  let scope3 = clones.find((scope) => /scope\s*3/i.test(scope.code));
  if (!scope3) {
    scope3 = { code: "Scope 3", value: 0, categories: [] };
    clones.push(scope3);
  }

  const ensureCategory = (code: string, name: string): SankeyCategory => {
    let cat = scope3.categories.find((c) => c.code === code);
    if (!cat) {
      cat = { code, value: 0, name, subcategories: [] };
      scope3.categories.push(cat);
    }
    return cat;
  };

  const supplierKg = breakdown.supplierTotalKg;
  if (supplierKg > 0) {
    const cat4 = ensureCategory("Category 4", "Category 4");
    const existing41 = cat4.subcategories.find(
      (sub) => sub.code === "Category 4.1",
    );
    if (existing41) {
      existing41.value += supplierKg;
    } else {
      cat4.subcategories.push({
        code: "Category 4.1",
        name: "Purchased goods & services",
        value: supplierKg,
      });
    }
    cat4.value += supplierKg;
    scope3.value += supplierKg;
  }
  if (Object.keys(breakdown.investorByCode).length) {
    const cat5 = ensureCategory("Category 5", "Category 5");
    for (const entry of Object.values(breakdown.investorByCode)) {
      if (entry.totalKg <= 0) continue;
      const existing = cat5.subcategories.find(
        (sub) => sub.code === entry.code,
      );
      if (existing) {
        existing.value += entry.totalKg;
      } else {
        cat5.subcategories.push({
          code: entry.code,
          name: entry.name,
          value: entry.totalKg,
        });
      }
      cat5.value += entry.totalKg;
      scope3.value += entry.totalKg;
    }
  }

  return clones;
}

function allocationMonths(
  breakdown: SupplierAllocationBreakdown,
): Map<string, number> {
  const allocatedByMonth = new Map<string, number>();
  for (const [key, kg] of Object.entries(breakdown.monthlyKg)) {
    allocatedByMonth.set(key, kg);
  }
  for (const entry of Object.values(breakdown.investorByCode)) {
    for (const [key, kg] of Object.entries(entry.monthlyKg)) {
      allocatedByMonth.set(key, (allocatedByMonth.get(key) ?? 0) + kg);
    }
  }

  return allocatedByMonth;
}

/** Yearly emissions are returned in tonnes, unlike the kg-based monthly APIs. */
export function mergeAllocationsIntoYear(
  rows: YearEmission[],
  breakdown: SupplierAllocationBreakdown | null,
): YearEmission[] {
  if (!breakdown) return rows;
  const years = new Map(rows.map((row) => [row.year, row.value]));
  for (const [month, kg] of allocationMonths(breakdown)) {
    const year = Number(month.slice(0, 4));
    years.set(year, (years.get(year) ?? 0) + kg / ALLOCATION_TCO2E_TO_KG);
  }
  return [...years]
    .sort(([a], [b]) => a - b)
    .map(([year, value]) => ({ year, value }));
}

export function mergeAllocationsIntoScopeMonth(
  response: EmissionsByScopeAndMonthResponse,
  breakdown: SupplierAllocationBreakdown | null,
): EmissionsByScopeAndMonthResponse {
  if (!breakdown) return response;
  const allocatedByMonth = allocationMonths(breakdown);
  const xaxis = monthAxis([
    ...(response.xaxis ?? []).map((x) => x.months),
    ...response.data.flatMap((row) => row.data.map((point) => point.name)),
    ...allocatedByMonth.keys(),
  ]);
  const data = response.data.map((scope) => ({
    ...scope,
    data: alignMonths(scope.data, xaxis),
  }));
  let scope3 = data.find(
    (scope) => scope.scope === 3 || /scope\s*3/i.test(scope.name),
  );
  if (!scope3) {
    scope3 = { id: 3, scope: 3, name: "Scope 3", data: alignMonths([], xaxis) };
    data.push(scope3);
  }
  for (const point of scope3.data)
    point.value += allocatedByMonth.get(monthKey(point.name)) ?? 0;
  return { ...response, data, xaxis };
}

export function mergeAllocationsIntoScopeCategory(
  response: EmissionsByScopeAndCategoryResponse,
  breakdown: SupplierAllocationBreakdown | null,
  protocol: EmissionProtocol = "iso",
): EmissionsByScopeAndCategoryResponse {
  if (!breakdown) return response;

  const allocationMonths = [
    ...Object.keys(breakdown.monthlyKg),
    ...Object.values(breakdown.investorByCode).flatMap((entry) =>
      Object.keys(entry.monthlyKg),
    ),
  ];
  const xaxis = monthAxis([
    ...(response.xaxis ?? []).map((x) => x.months),
    ...response.data.flatMap((row) => row.data.map((point) => point.name)),
    ...allocationMonths,
  ]);
  const clones = {
    ...response,
    xaxis,
    data: response.data.map((cat) => ({
      ...cat,
      data: alignMonths(cat.data, xaxis),
    })),
  };
  const months = xaxis.map((x) => x.months);
  let syntheticId = -1000;

  const makeRow = (
    name: string,
    monthlyKg: Record<string, number>,
  ): (typeof clones.data)[number] => ({
    category: syntheticId,
    id: syntheticId,
    name,
    data: months.map((month) => ({
      name: month,
      value: monthlyKg[monthKey(month)] ?? 0,
    })),
  });

  const addRow = (
    code: string,
    name: string,
    monthlyKg: Record<string, number>,
  ) => {
    const existing = clones.data.find(
      (row) => row.name === code || row.name.startsWith(`${code} - `),
    );
    if (existing) {
      for (const point of existing.data)
        point.value += monthlyKg[monthKey(point.name)] ?? 0;
    } else {
      clones.data.push(makeRow(`${code} - ${name}`, monthlyKg));
      syntheticId -= 1;
    }
  };

  if (breakdown.supplierTotalKg > 0) {
    addRow(
      protocol === "ghg" ? "Scope 3.1" : "Category 4.1",
      "Purchased goods & services",
      breakdown.monthlyKg,
    );
  }
  for (const entry of Object.values(breakdown.investorByCode)) {
    if (entry.totalKg <= 0) continue;
    addRow(investmentCode(entry, protocol), entry.name, entry.monthlyKg);
  }

  return clones;
}
